import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from '../core/environment';

interface Student {
  id: string;
  membershipCode: string;
  fullName: string;
  mobile: string;
  email?: string;
  status: number;
  joiningDate: string;
}

interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

@Component({
  selector: 'app-students',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="page-header">
      <div>
        <h2>Student Management</h2>
        <p>Fast admissions, profiles, memberships, and sport enrollments.</p>
      </div>
    </div>

    <section class="split-grid">
      <form class="panel form-panel" [formGroup]="form" (ngSubmit)="create()">
        <h3>Add Student</h3>
        <div class="two-col">
          <label>First Name <input formControlName="firstName"></label>
          <label>Last Name <input formControlName="lastName"></label>
        </div>
        <div class="two-col">
          <label>Gender <input formControlName="gender"></label>
          <label>Mobile <input formControlName="mobile"></label>
        </div>
        <label>Email <input formControlName="email"></label>
        <label>Emergency Contact <input formControlName="emergencyContactMobile"></label>
        <label>Medical Notes <textarea formControlName="medicalNotes"></textarea></label>
        <button class="primary-action" type="submit" [disabled]="form.invalid">Create Student</button>
      </form>

      <article class="panel">
        <div class="table-toolbar">
          <h3>Students</h3>
          <input placeholder="Search students" [value]="search()" (input)="search.set($any($event.target).value); load()">
        </div>
        <table>
          <thead><tr><th>Membership</th><th>Name</th><th>Mobile</th><th>Status</th></tr></thead>
          <tbody>
            @for (student of students(); track student.id) {
              <tr>
                <td>{{ student.membershipCode }}</td>
                <td>{{ student.fullName }}</td>
                <td>{{ student.mobile }}</td>
                <td><span class="status-chip">Active</span></td>
              </tr>
            }
          </tbody>
        </table>
      </article>
    </section>
  `
})
export class StudentsComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);
  readonly students = signal<Student[]>([]);
  readonly search = signal('');
  readonly form = this.fb.nonNullable.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    gender: ['Male', Validators.required],
    dateOfBirth: [null as string | null],
    mobile: ['', Validators.required],
    email: [''],
    addressLine1: [''],
    city: [''],
    emergencyContactName: [''],
    emergencyContactMobile: [''],
    medicalNotes: [''],
    sportId: [null as string | null],
    batchId: [null as string | null],
    levelId: [null as string | null],
    membershipTypeId: [null as string | null],
    feePlanId: [null as string | null],
    startDate: [null as string | null]
  });

  ngOnInit(): void {
    this.load();
  }

  create(): void {
    if (this.form.invalid) return;
    this.http.post(`${environment.apiUrl}/students`, this.form.getRawValue()).subscribe(() => {
      this.form.reset({ gender: 'Male' });
      this.load();
    });
  }

  load(): void {
    this.http.get<PagedResult<Student>>(`${environment.apiUrl}/students?search=${this.search()}&pageSize=50`).subscribe((result) => this.students.set(result.items));
  }
}
